import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { floorPlanAPI, projectAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, Map, Eye, Edit, Trash2, Building2, Layers, 
  CheckCircle, XCircle, AlertCircle, Home
} from 'lucide-react';

const FloorPlansPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [floorPlans, setFloorPlans] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('all');
  
  const isBuilder = user?.role === 'builder_admin';
  const basePath = user?.role === 'builder_admin' ? '/builder' : '/partner';

  useEffect(() => {
    loadData();
  }, [selectedProject]);

  const loadData = async () => {
    try {
      const [fpRes, projRes] = await Promise.all([
        floorPlanAPI.list(selectedProject !== 'all' ? selectedProject : undefined),
        projectAPI.list()
      ]);
      setFloorPlans(fpRes.data);
      setProjects(projRes.data);
    } catch (error) {
      toast.error('Failed to load floor plans');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this floor plan?')) return;
    
    try {
      await floorPlanAPI.delete(id);
      toast.success('Floor plan deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete floor plan');
    }
  };

  const getStats = (fp) => {
    const stats = { available: 0, sold: 0, blocked: 0, total: 0 };
    (fp.units || []).forEach(u => {
      stats.total++;
      const status = u.status || 'available';
      if (stats.hasOwnProperty(status)) stats[status]++;
    });
    return stats;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="floor-plans-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Map className="h-7 w-7 text-blue-600" />
            Floor Plan Mapper
          </h1>
          <p className="text-muted-foreground">
            {isBuilder ? 'Create and manage floor plan mappings' : 'View flat availability on floor plans'}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by project" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {projects.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {isBuilder && (
            <Button onClick={() => navigate(`${basePath}/floor-plans/new`)}>
              <Plus className="h-4 w-4 mr-2" />
              New Floor Plan
            </Button>
          )}
        </div>
      </div>

      {/* Floor Plans Grid */}
      {floorPlans.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Layers className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No floor plans yet</p>
            <p className="text-muted-foreground mb-4">
              {isBuilder ? 'Create your first floor plan to map units' : 'No floor plans available'}
            </p>
            {isBuilder && (
              <Button onClick={() => navigate(`${basePath}/floor-plans/new`)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Floor Plan
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {floorPlans.map(fp => {
            const stats = getStats(fp);
            const project = projects.find(p => p.id === fp.project_id);
            
            return (
              <Card 
                key={fp.id} 
                className="cursor-pointer hover:border-blue-300 transition-colors group"
                onClick={() => navigate(`${basePath}/floor-plans/${fp.id}`)}
              >
                {/* Thumbnail */}
                <div className="relative h-40 bg-muted overflow-hidden rounded-t-lg">
                  {fp.image_url ? (
                    <img 
                      src={fp.image_url} 
                      alt={fp.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <Building2 className="h-12 w-12 text-muted-foreground" />
                    </div>
                  )}
                  
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button size="sm" variant="secondary">
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    {isBuilder && (
                      <Button size="sm" variant="secondary">
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    )}
                  </div>
                </div>
                
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold">{fp.name}</h3>
                      <p className="text-sm text-muted-foreground">{project?.name}</p>
                    </div>
                    {isBuilder && (
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-destructive opacity-0 group-hover:opacity-100"
                        onClick={(e) => handleDelete(fp.id, e)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  
                  {fp.tower && (
                    <Badge variant="outline" className="mb-2">{fp.tower}</Badge>
                  )}
                  
                  {/* Stats */}
                  <div className="flex items-center gap-3 mt-3 text-xs">
                    <span className="flex items-center gap-1">
                      <Home className="h-3 w-3" />
                      {stats.total} units
                    </span>
                    <span className="flex items-center gap-1 text-green-600">
                      <CheckCircle className="h-3 w-3" />
                      {stats.available}
                    </span>
                    <span className="flex items-center gap-1 text-red-600">
                      <XCircle className="h-3 w-3" />
                      {stats.sold}
                    </span>
                    <span className="flex items-center gap-1 text-yellow-600">
                      <AlertCircle className="h-3 w-3" />
                      {stats.blocked}
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FloorPlansPage;
